import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * MockupCatalog — the Mockups "Select" workspace. A shared CatalogTree (Product → Brand →
 * Model) over the templates SYNCED to THIS device, plus a per-model template pane
 * (BatchContents) that checks templates into the shared batch. The mockups export tree
 * (`Mockups/<Type>/<Brand>/<Model>/<View>/<Design>`) is unchanged: every checked template flows
 * through `selectionFromFunnel(...)` carrying the tree-known Product/Brand/Model labels.
 *
 * Source of truth here is the device-local synced store (`listSyncedTemplates`), RECONCILED
 * against the cloud catalogue on mount, on every Refresh and on every auto-sync delivery
 * (`templates/syncedReconcile.ts`): a template the cloud gained is fetched in full and saved
 * here, one it lost (deleted/archived) is evicted with its baked mock-ups, and a changed one
 * is overwritten. Synced templates carry their geometry, so selecting one is still instant and
 * creating a mockup still works fully offline — and when the cloud is unreachable the cached
 * list stays on screen behind a visible "offline" note instead of silently going stale.
 *
 * Before that reconcile, this list was a WRITE-ONLY cache that only the Templates page fed:
 * new templates never appeared, deleted ones never left, and the page's own Refresh re-read
 * the same unchanged store.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import { ActionBar } from "../ui/ActionBar.js";
import { InlineRetry } from "../ui/InlineRetry.js";
import { useHydrationStore } from "../stores/hydrationStore.js";
import { mockupStoreDegraded, subscribeMockupStoreDegraded } from "../stores/mockupStore.js";
import { useSessionStore } from "../stores/session.js";
import { useSelectionStore, selectionFromFunnel, type TemplateSelection } from "../stores/selection.js";
import { Icon } from "../ui/Icon.js";
import type { CatalogNameLists } from "../catalog/names.js";
import { useBatchStore } from "../stores/batch.js";
import { toDesignInputs } from "../batch/upload.js";
import { CatalogTree } from "./catalog/CatalogTreeView.js";
import type { ModelNode } from "./catalog/catalogTree.js";
import { hydrateBakedAssets, ioHydrateDeps, type BakedAssetRefsMap } from "../templates/bakedSync.js";
import { forceSyncTemplate, savedViews, viewTypes } from "../templates/forceSync.js";
import { MissingMockups, useMissingMockups } from "./MissingMockups.js";
import { announceBakeAbsence } from "../templates/bakeDiagnostics.js";
import { listSyncedTemplates, listSyncedPhoneModels, listSyncedCaseStyles } from "../templates/localTemplates.js";
import { reconcileSyncedTemplates, reconcileMessage, syncOneTemplate } from "../templates/syncedReconcile.js";
import { readHiddenLocalIds } from "../templates/deviceHidden.js";
import { presenceBadge } from "../templates/devicePresence.js";
import { isMockupOnlyTemplate } from "../templates/wireMasks.js";
import { subscribeTemplates } from "../sync/autoSync.js";
import { SyncFreshness } from "../sync/SyncFreshness.js";
import type { BrandProfile, CaseStyleOption, DesignInput, PhoneModelOption, PickedFile, ProductTemplate } from "@genz/platform";
import "./mockups.css";

/** Compact codes summary for the Select tab's design-count line: the first 3 design
 *  codes (falling back to filename when a design has no code), then "+N more". */
function designCodesSummary(designs: DesignInput[]): string {
  const codes = designs.map((d) => d.code ?? d.filename);
  const shown = codes.slice(0, 3);
  const rest = codes.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest} more` : shown.join(", ");
}

/** A mock-up only template has no print area to place a design in, so the batch never offers it. */
const offered = (list: ProductTemplate[]) => list.filter((t) => !isMockupOnlyTemplate(t));

const PICK_OPTS = {
  accept: ["image/png", "image/jpeg", "image/webp", "image/gif", "image/bmp", "image/tiff"],
  multiple: true,
  maxBytes: 32 * 1024 * 1024,
};

export function MockupCatalog({ onReady }: { onReady?: () => void }) {
  const { client, io, engine, logger } = useServices();
  const isAdmin = useSessionStore((s) => s.isAdmin());
  const [templates, setTemplates] = useState<ProductTemplate[]>([]);
  /** Which rows have a FULL local record. A row NOT in here is a cloud summary: it shows with
   *  a "Not synced here" marker and costs nothing, and its record is fetched only if the
   *  operator actually ticks it (F-8). */
  const [onDeviceIds, setOnDeviceIds] = useState<ReadonlySet<string>>(() => new Set());
  const [phoneModels, setPhoneModels] = useState<PhoneModelOption[]>([]);
  /** The case-style catalog, read from the SAME local cache as the phone models (F-7 /
   *  review-2 item 1). It names the style in the sheet's `Case Style` column and in the SKU;
   *  without it a funnel run shipped the TEMPLATE's name in both. */
  const [caseStyles, setCaseStyles] = useState<CaseStyleOption[]>([]);
  const [brands, setBrands] = useState<BrandProfile[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** True until the cached rows are on screen — NOT until the network answers. The page must
   *  never sit on a placeholder waiting for the cloud (F-10): the device already knows what it
   *  has, so it paints that first and the reconcile edits it underneath. */
  const [loading, setLoading] = useState(true);
  /** True while a reconcile runs with rows already on screen. Drives the quiet activity
   *  indicator, so a background re-list is never invisible. */
  const [reloading, setReloading] = useState(false);
  /** The reconcile's own sentence: what the last pass changed, the OFFLINE note, or the
   *  suspect-empty-list note. Same `role="status"` treatment as the Templates page's
   *  `templates-local-msg` — a list that quietly rewrote itself (or quietly could not) has to
   *  say so. */
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  /** `done / total` while Sync downloads records + bakes; null when it is not running. */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /** The templates whose record is being fetched right now (a ticked cloud-only row, or the
   *  Sync sweep) — the row renders busy rather than looking inert. */
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Crux 3 — ONE reconcile in flight, ever.
   *
   * `load()` is called from four places: mount, the header control, every auto-sync delivery
   * (a 60s ticker AND every window focus), and Sync's own first step. Left unguarded those
   * overlap, and two passes that both decide a row is gone both try to evict it — the second
   * failing on a record the first already removed, which surfaces as a spurious "could not be
   * removed". A caller that arrives while a pass is running JOINS it instead of starting
   * another.
   */
  const inFlight = useRef<Promise<ProductTemplate[]> | null>(null);
  // Read the current row count inside `load()` without adding `templates` to its deps (which
  // would rebuild `load` — and re-fire the subscribe effect — on every data change).
  const hasRowsRef = useRef(false);
  hasRowsRef.current = templates.length > 0;

  /**
   * Paint what the device has, THEN reconcile it against the cloud.
   *
   * The two halves are deliberately sequential in effect and not in perceived cost: the cached
   * rows and the cached name lists come from IndexedDB and land immediately, so the tree is
   * usable while the listing is still being paged. The reconcile never throws and never evicts
   * on an unproven absence — an unreadable listing comes back `offline` with the same rows,
   * which `syncMsg` then states.
   */
  /** Resolves to the rows ON THIS DEVICE — the only ones Sync may re-pull. */
  const runLoad = useCallback(async (): Promise<ProductTemplate[]> => {
    setError(null);
    if (hasRowsRef.current) setReloading(true);
    try {
      // 1. THE CACHED PAINT. No network on this path.
      const [cached, phones, styles] = await Promise.all([
        listSyncedTemplates(io).catch(() => [] as ProductTemplate[]),
        listSyncedPhoneModels(io),
        listSyncedCaseStyles(io),
      ]);
      setPhoneModels(phones ?? []);
      setCaseStyles(styles ?? []);
      if (cached.length > 0) {
        setTemplates(offered(cached));
        setOnDeviceIds(new Set(cached.map((t) => t.templateId)));
      }
      setLoading(false);

      // 2. THE RECONCILE.
      const outcome = await reconcileSyncedTemplates(io, client, {
        engine, skipIds: readHiddenLocalIds(), canSeeAllStatuses: isAdmin,
      });
      setTemplates(offered(outcome.templates));
      setOnDeviceIds(outcome.onDeviceIds);
      setSyncMsg(reconcileMessage(outcome));
      if (outcome.offline) {
        logger?.breadcrumb("sync", "Mockups · cloud list unreachable, showing the cached list", {
          error: outcome.error, cached: outcome.templates.length,
        });
      }
      // A per-row failure is reported on screen by `reconcileMessage`; the causes belong in
      // the log too, where the operator can read the whole list.
      for (const f of outcome.failed) {
        logger?.log("warn", `Mockups · reconcile could not finish one template — ${f}`);
      }
      // Seller brands (logo overlay) are a separate, optional concern — best-effort.
      try {
        const bs = await client.listBrands?.();
        if (bs) setBrands(bs.filter((b) => (b.status ?? "approved") === "approved"));
      } catch { /* no brands → render without a logo overlay */ }
      return outcome.templates.filter((t) => outcome.onDeviceIds.has(t.templateId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return [];
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [io, client, engine, logger, isAdmin]);

  const load = useCallback((): Promise<ProductTemplate[]> => {
    if (inFlight.current) return inFlight.current;
    const run = runLoad().finally(() => { inFlight.current = null; });
    inFlight.current = run;
    return run;
  }, [runLoad]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Fetch ONE template's full record onto this device (and hydrate its bake), because the
   * operator asked for it — by ticking a cloud-only row, or by pressing Sync.
   *
   * This is the ONLY place a `getTemplate` happens for a row the page is merely showing. The
   * union exists so the whole catalogue can be SEEN for the price of one listing; downloading
   * it on everyone's behalf would put the price straight back.
   */
  const track = useCallback(async (
    templateId: string, fetchFull: () => Promise<ProductTemplate>,
  ): Promise<ProductTemplate> => {
    setPending((prev) => new Set(prev).add(templateId));
    try {
      const full = await fetchFull();
      setTemplates((prev) => prev.map((r) => (r.templateId === full.templateId ? full : r)));
      setOnDeviceIds((prev) => new Set(prev).add(full.templateId));
      return full;
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(templateId);
        return next;
      });
    }
  }, []);
  const pullOne = useCallback(
    (t: ProductTemplate) => track(t.templateId, () => syncOneTemplate(io, client, t.templateId)),
    [track, io, client],
  );

  /**
   * The ONE page-level verb (F-11): re-list from the cloud, then re-download every template
   * ALREADY ON THIS DEVICE — fresh record plus a FORCED mock-up download. A cloud-only row is
   * never pulled here; ticking it is what downloads it (F-8).
   *
   * The guard is taken BEFORE the first `await`: a busy flag set after one is not a guard, and
   * a double-press would have run two sweeps over the same rows. It is a REF for the same
   * reason — `progress` is state, and two presses in one tick both read the old value.
   *
   * The guard is about a second SYNC, not about `load()`. A press while the page's own
   * background reconcile is listing WAITS for that load and then lists again, because a
   * listing that started before the press cannot answer "what is in the cloud now".
   */
  const syncing = progress !== null;
  const syncLock = useRef(false);
  const syncNow = useCallback(async () => {
    if (syncLock.current) return; // BEFORE any await — see above
    syncLock.current = true;
    setProgress({ done: 0, total: 0 });
    let failed = 0;
    let total = 0;
    try {
      if (inFlight.current) await inFlight.current; // `runLoad` never rejects
      const rows = await load();
      total = rows.length;
      setProgress({ done: 0, total });
      for (const row of rows) {
        try {
          await track(row.templateId, async () => (await forceSyncTemplate(io, client, row.templateId)).template);
        } catch (e) {
          failed++;
          logger?.log("error", `Mockups · Sync could not finish "${row.name}"`, {
            templateId: row.templateId, error: e instanceof Error ? e.message : String(e),
          });
        }
        setProgress((p) => (p ? { done: p.done + 1, total: p.total } : p));
      }
    } finally {
      syncLock.current = false;
      setProgress(null);
    }
    setSyncMsg((prev) => {
      const tail = failed > 0
        ? `${failed} template${failed === 1 ? "" : "s"} could not be downloaded (see Settings → Observability → Error log).`
        : `${total} template${total === 1 ? "" : "s"} on this device re-downloaded.`;
      return prev ? `${prev} ${tail}` : `Synced with the cloud — ${tail}`;
    });
  }, [load, track, io, client, logger]);

  const lists = useMemo(() => ({ phoneModels, caseStyles }), [phoneModels, caseStyles]);

  // Wave-2 Task 5: the app-wide ticker re-hydrates this device's synced templates to their
  // latest version BEFORE it delivers, so re-reading the LOCAL synced store on delivery
  // surfaces a new version made on another device here — without a manual reload.
  useEffect(() => subscribeTemplates(() => { void load(); }), [load]);

  return (
    <section className="mockup-funnel" data-testid="mockup-catalog">
      {/* MI-10 / AUD-7 §3.7 (redundant words): this used to lead with an <h2> reading
          "Mockup Generator" — Title Case, the only one in the app — directly under a page
          title that says the same thing and a tab bar whose first tab is "Select". The title
          is gone; its InfoHint and the refreshing indicator stay, because those carry
          information the tab labels do not. */}
      <header className="mockup-funnel__head">
        <div className="mockup-funnel__title row">
          {/* The copy is the new truth: the list is the whole catalogue, and "here" is about
              which rows are already downloaded, not which rows exist. */}
          <InfoHint text="Open a product, brand & model, tick the templates you want, add a design folder and export. Every template in your catalogue is listed; the ones marked “Not synced here” download when you tick them." />
          {reloading && (
            <span className="mockup-funnel__refreshing faint" role="status" data-testid="mockup-catalog-refreshing">
              <span className="btn-spinner" aria-hidden="true" /> Loading…
            </span>
          )}
        </div>
        <div className="row">
          {/* F-11: ONE page-level verb. The tree's own Refresh is not wired on this surface —
              two controls a thumb apart, one re-listing and one re-listing AND downloading,
              is the "two words for one action" defect the design gate exists to stop.
              The BUSY state is `aria-busy` + `disabled` + a spinner, and the count lives in
              its own status line below: G-21 keeps `Working…` to the one file that is already
              ticketed to lose it, and its ticket says to reach for `aria-busy` first — so a
              new control does not add a sixth loading verb to get there. */}
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => void syncNow()}
            disabled={syncing}
            aria-busy={syncing}
            data-testid="mockup-catalog-sync"
            title="Re-download the templates and mock-ups on this device. Only templates already on this device are synced."
          >
            {syncing && <span className="btn-spinner" aria-hidden="true" />}
            Sync
          </button>
          <SyncFreshness />
        </div>
      </header>
      {/* The sweep's own progress, out of the button label: a template literal in a label is
          invisible to the G-21 copy sweep, and the count belongs beside the work anyway. */}
      {progress && (
        <p className="panel-sub" role="status" data-testid="mockup-catalog-progress">
          Downloading {progress.done.toLocaleString()} of {progress.total.toLocaleString()}…
        </p>
      )}
      {error && <p className="banner" role="alert" data-testid="mockup-catalog-error"><ErrorNotice detail={error} /></p>}
      {/* What the last reconcile changed — or, when the cloud could not be reached, that this
          list is the last synced one. Never silent: a list that rewrote itself and a list that
          could NOT be rewritten look identical on screen otherwise. */}
      {syncMsg && (
        <p className="panel-sub" role="status" data-testid="mockup-catalog-sync-msg">{syncMsg}</p>
      )}
      {loading && templates.length === 0 ? (
        <EmptyState icon="image" title="Loading your templates…" testId="mockup-catalog-loading" />
      ) : templates.length === 0 ? (
        /* WS-B B-9 / AUD-6 IMP-12 — ONE empty state, naming the one thing missing.
           Nothing synced used to produce THREE at once: the rail's own "No synced templates
           yet…", the pane's "Pick a model on the left to see its templates." and the
           footer's "No designs yet" beside a "0 templates selected" counter. Three ways of
           saying one thing, and the only one that named a next step was buried in the rail.
           Mounting the tree at all is what produced the other two, so it is not mounted:
           there is no tree to navigate and no model to pick. */
        <EmptyState
          icon="image"
          title="No templates yet"
          hint="Templates you create or are given appear here. The Mock-up Generator keeps the ones you use on this device, so creating a mock-up works offline."
          testId="mockup-catalog-empty"
          action={
            <button
              type="button"
              className="btn btn--primary empty-state__action"
              onClick={() => { if (typeof window !== "undefined") window.location.hash = "browse"; }}
              data-testid="mockup-goto-templates"
            >
              Go to Templates
            </button>
          }
        />
      ) : (
        /* `emptyLabel` is passed ONLY in the case above — which no longer reaches the tree —
           so the rail's empty text can never appear beside the pane's again. */
        <CatalogTree templates={templates} phoneModels={phoneModels}>
          {(model) => (
            <BatchContents model={model} brands={brands} lists={lists} onReady={onReady}
                           onDeviceIds={onDeviceIds} pendingIds={pending} onPull={pullOne} />
          )}
        </CatalogTree>
      )}
    </section>
  );
}

function BatchContents({
  model, brands, lists, onReady, onDeviceIds, pendingIds, onPull,
}: {
  model: ModelNode | null;
  brands: BrandProfile[];
  /** The locally-cached catalog lists, so the sheet's product columns and the SKU carry the
   *  operator's own names rather than the template's (F-7 / review-2 item 1). */
  lists: CatalogNameLists;
  onReady?: () => void;
  /** Rows with a FULL local record. Anything else is a cloud summary with no geometry. */
  onDeviceIds: ReadonlySet<string>;
  /** Rows whose record is being fetched right now. */
  pendingIds: ReadonlySet<string>;
  /** Fetch + persist one row's full record, returning it. */
  onPull: (t: ProductTemplate) => Promise<ProductTemplate>;
}) {
  const { io, logger } = useServices();
  const addToBatch = useSelectionStore((s) => s.addToBatch);
  const removeFromBatch = useSelectionStore((s) => s.removeFromBatch);
  const clearSelection = useSelectionStore((s) => s.clear);
  const batchSelections = useSelectionStore((s) => s.batchSelections);
  const batch = useBatchStore();
  const setSellerBrand = useSelectionStore((s) => s.setSellerBrand);
  // Seeded from the STORE, not "": this pane remounts on every model/product switch, so a
  // blank initial value made the dropdown disagree with the brand the staged templates
  // actually carry (the operator saw "Acme", the render saw none).
  const [sellerBrandId, setSellerBrandId] = useState(
    () => useSelectionStore.getState().batchSelections[0]?.context.sellerBrandId ?? "",
  );
  // Derived from the STORE, not local state: the count now survives a remount (switching
  // model/product used to show "No designs yet" over a fully-staged batch) and it stays
  // truthful when the Batch tab removes designs.
  const designCount = batch.designs.length;
  const [error, setError] = useState<string | null>(null);
  // Live folder-import progress (Task 7): a large design folder is read file-by-file, so we
  // surface a "done / total" row instead of a frozen-looking UI. null = not importing.
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const checkedIds = new Set(batchSelections.map((b) => b.templateId));

  // Fire-and-forget baked-asset hydration (best-effort — only needed later at render time, so it
  // never blocks selection). The `useHydrationStore` status drives the per-row spinner / retry.
  // The failure is NOT swallowed: it's caught (so the promise never rejects unhandled) but logged
  // as a breadcrumb with structured detail, so a hydrate that failed is visible in the trail.
  const hydrate = useCallback((t: ProductTemplate, opts?: { force?: boolean }) => {
    const refs = t.currentVersion?.bakedAssets as BakedAssetRefsMap | null | undefined;
    const versionId = t.currentVersion?.versionId;
    // WS-P / AUD-8 LB-8 (ii): this used to be a bare `if (!refs || !versionId) return;` — the
    // single most common way a staged template reached the render path with nothing to
    // composite, and it left no trace at all. The control flow is unchanged (there is genuinely
    // nothing to download); it now CLASSIFIES the reason and says it once per (version, cause).
    if (!versionId) {
      announceBakeAbsence(logger, "no-version", { templateId: t.templateId, versionId, templateName: t.name });
      return;
    }
    if (!refs) {
      announceBakeAbsence(logger, "not-published", { templateId: t.templateId, versionId, templateName: t.name });
      return;
    }
    // Hydrate every view this template SAVED (its version geometry's `view_type`s), so a
    // multi-view template's non-back views land locally too — not just the primary view.
    // WS-ERR F-12: a RETRY starts from a clean slate. Without this the banner from the first
    // failure stayed on screen after a Retry that worked, telling the operator to retry
    // something that no longer needed it — and the sentence named a template that was fine.
    setError(null);
    void hydrateBakedAssets(versionId, refs, ioHydrateDeps(io), { ...opts, views: savedViews(t) })
      .catch((e) => {
        const message = e instanceof Error ? e.message : String(e);
        // Silent-failure sweep: this was a breadcrumb, at `action` level, and nothing else. The
        // row's own `InlineRetry` does come up from the failure memo — but the operator has to
        // be looking at that row, and a template whose layers never downloaded renders
        // design-only (a case-less mock-up). `TemplateListPage.tsx` answers the identical
        // failure with a log AND `setError`; this is the same answer, in the same words.
        logger?.log("error", "Mockups · a template's baked layers could not be downloaded — it would render design-only", {
          step: "hydrateBakedAssets", templateId: t.templateId, versionId, error: message,
        });
        // F-12: the FOOTER's `mockup-bake-blocked` banner already names every ticked template
        // whose bake failed, and it is the one that gates the run. Saying the same thing twice
        // on one screen trains the operator to read neither, so this banner is for a template
        // that is NOT ticked — the failure they would otherwise only meet later, after ticking
        // it. The footer reads the same `hydrationFailure` memo (`findMissingMockups`).
        if (!useSelectionStore.getState().batchSelections.some((b) => b.templateId === t.templateId)) {
          setError(`“${t.name}” could not download its mock-up layers (${message}). It would render as the design alone — use Retry on its row before starting a run.`);
        }
      });
  }, [io, logger]);

  const unstage = useCallback((templateId: string) => {
    removeFromBatch(templateId);
    // `addToBatch` also sets the single `selection` singleton (ExportWorkspace reads
    // `batchSelections[0] ?? selection` for Batch and `selection` directly for Export),
    // so trimming `batchSelections` alone would leave the just-removed template alive in
    // both tabs. When the batch is now empty, clear the singleton too. Store updates are
    // async, so read the fresh state (regression guard — old MockupFunnel did the same).
    if (useSelectionStore.getState().batchSelections.length === 0) clearSelection();
  }, [removeFromBatch, clearSelection]);

  const toggle = useCallback(async (t: ProductTemplate, on: boolean) => {
    if (!model) return;
    if (!on) {
      unstage(t.templateId);
      return;
    }
    setError(null);
    try {
      // A row on THIS DEVICE already carries its geometry, so the selection is built with NO
      // network call — that is what makes selecting a synced template instant, and it is
      // unchanged. A CLOUD-ONLY row is a summary with no geometry, and this tick is the
      // moment the operator asked for it: the record is downloaded now, once, for this one
      // template. Nothing is ever fetched for a row that is merely on screen (F-8).
      const record = onDeviceIds.has(t.templateId) ? t : await onPull(t);
      const customerId = useSessionStore.getState().session?.customerId ?? "self";
      addToBatch(selectionFromFunnel(record, customerId, {
        productType: model.product, brand: model.brand, model: model.label,
        phoneSlug: model.slug, sellerBrandId: sellerBrandId || undefined,
      }, lists));
      // Rehydrate the baked layered-mockup assets into the in-memory mockup store (used by
      // the render worker). Best-effort + non-blocking — only needed later at render time.
      hydrate(record);
    } catch (e) {
      setError(
        `“${t.name}” could not be downloaded to this device (${e instanceof Error ? e.message : String(e)}). ` +
        "It stays unticked — tick it again to retry.",
      );
    }
  }, [model, addToBatch, unstage, sellerBrandId, hydrate, lists, onDeviceIds, onPull]);

  /** Pick (or clear) the seller brand whose logo is merged into every mockup.
   *  Applied to the templates ALREADY checked in, not just the ones ticked afterwards: the
   *  Active-template auto-check below fires the moment a model is opened — before the
   *  operator can reach this dropdown — so a forward-only pick meant the DEFAULT flow
   *  (open model → choose brand → create) rendered every mockup with no logo at all. */
  const changeSellerBrand = useCallback((id: string) => {
    setSellerBrandId(id);
    setSellerBrand(id || undefined);
  }, [setSellerBrand]);

  // Auto-check the model's Active template the first time this model is opened. The dedup
  // key is namespaced by tree position (product::brand::modelKey), NOT the raw `model.key`
  // (a phone-model id) — the same phone model appears under multiple products (e.g. iPhone
  // 15 under both clear_case and phone_case, sharing key "ip15"), so a bare-key guard would
  // skip the Active-default for the second product. Mirrors CatalogTreeView's selection key.
  //
  // The done-set lives in the SELECTION STORE, not component state: this pane unmounts when
  // you leave the funnel (or switch product), so a remount-local guard forgot that the model
  // had already been offered and silently RE-ADDED the Active template the operator had
  // deliberately unticked.
  const markAutoChecked = useSelectionStore((s) => s.markAutoChecked);
  const autoKey = model ? `${model.product}::${model.brand}::${model.key}` : "";
  useEffect(() => {
    if (!model || useSelectionStore.getState().autoCheckedKeys.includes(autoKey)) return;
    // `model.templates` is already exactly this node's templates — pick the Active default
    // directly by status (no re-filter by id).
    const active =
      model.templates.find((t) => t.currentVersion?.status === "Active") ??
      model.templates.find((t) => t.currentVersion?.status === "Approved") ??
      model.templates[0];
    markAutoChecked(autoKey);
    if (active && !checkedIds.has(active.templateId)) void toggle(active, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoKey]);

  const pickFolder = useCallback(async () => {
    setError(null);
    try {
      const files: PickedFile[] = await io.pickDirectory({
        ...PICK_OPTS,
        onProgress: (done, total) => setImportProgress({ done, total }),
      });
      if (!files || files.length === 0) return;
      // APPEND, don't replace: picking a second folder used to wipe the first folder's
      // designs AND re-tick every design the operator had unticked on the Batch tab.
      // `addDesigns` keeps the staged list + its tick state and skips already-staged files.
      batch.addDesigns(toDesignInputs(files));
      logger?.breadcrumb("action", "Mockups · design folder picked", { count: files.length });
    } catch (e) {
      logger?.log("error", "Mockups · design-folder pick failed", { error: e });
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      // Always clear the progress row — on success, cancel (empty), or error.
      setImportProgress(null);
    }
  }, [io, logger, batch]);

  const importing = importProgress !== null;

  /**
   * WS-P / AUD-8 LB-8: staged templates whose mock-up is not on this device — a failed
   * download, or a declared bake that never landed. P-1 makes the render refuse rather than
   * draw a bare product; this stops the handoff before the wasted run, and offers the download
   * right here.
   */
  const missing = useMissingMockups(batchSelections.map((b) => ({
    templateId: b.templateId, versionId: b.versionId, name: b.name,
    views: viewTypes(b.templateVersion.payload?.views), bakeExpected: b.bakeExpected,
  })));
  /* F-9 residual — SUBSCRIBED, not read during render. A module-level read only reached the
     screen when something else happened to re-render this component, so the first failed write
     of a session could sit unreported until the operator clicked something. */
  const storeDegraded = useSyncExternalStore(
    subscribeMockupStoreDegraded,
    mockupStoreDegraded,
    mockupStoreDegraded,
  );
  // Ticking is never gated (selection stays instant); the HANDOFF to the run is. Naming the
  // templates rather than silently dropping them: a ticked template that quietly vanished from
  // a run would be a new silent failure inside the wave whose subject is silent failures.
  const canProceed = batchSelections.length > 0 && designCount > 0 && missing.length === 0;

  return (
    <div className="mockup-pane" data-testid="mockup-batch-contents">
      {error && <p className="banner" role="alert"><ErrorNotice detail={error} /></p>}
      {!model ? (
        <p className="faint">Pick a model on the left to see its templates.</p>
      ) : model.templates.length === 0 ? (
        <p className="faint">No templates for {model.label} yet.</p>
      ) : (
        <ul className="mockup-templates panel-scroll" data-testid="mockup-template-list">
          {model.templates.map((t) => (
            <TemplateRow key={t.templateId} t={t} on={checkedIds.has(t.templateId)}
                         offDevice={!onDeviceIds.has(t.templateId)}
                         pulling={pendingIds.has(t.templateId)}
                         onToggle={(next) => void toggle(t, next)} onRetry={() => hydrate(t, { force: true })} />
          ))}
        </ul>
      )}

      {/* WS-B B-9 / IMP-12: the footer appears WITH a model. Before a model is picked it
          counted zeroes ("No designs yet", "0 templates selected") underneath a line already
          saying there was nothing to act on — a third empty state wearing a toolbar. Its
          controls (seller brand, design folder, Continue) are all about the templates you
          have just ticked, so they have nothing to say until there are some. */}
      {model && (
      <div className="mockup-footer card">
        {brands.length > 0 && (
          <label className="mockup-footer__field">
            <span>Seller brand <small className="faint">(optional)</small></span>
            <select className="input" value={sellerBrandId} onChange={(e) => changeSellerBrand(e.target.value)}
                    data-testid="mockup-seller-brand-select">
              <option value="">No brand logo</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
        )}
        <div className="mockup-footer__field">
          <button type="button" className="btn" onClick={() => void pickFolder()} data-testid="mockup-pick-folder"
                  disabled={importing} aria-busy={importing}>
            {/* Honest label: `pickFolder` APPENDS (see above) — it never replaces the
                staged designs, which "Change folder" promised. */}
            {designCount > 0 ? "Add another folder" : "Choose design folder"}
          </button>
          <span className="faint" data-testid="mockup-design-count">
            {designCount > 0 ? `${designCount} design${designCount === 1 ? "" : "s"}` : "No designs yet"}
          </span>
          {designCount > 0 && (
            <span className="faint" data-testid="mockup-design-codes">
              {designCodesSummary(batch.designs)}
            </span>
          )}
        </div>
        {importProgress && (
          <div className="gen-progress" data-testid="import-progress" role="status" aria-live="polite">
            <div className="gen-progress__head">
              <span>Importing designs… {importProgress.done.toLocaleString()} / {importProgress.total.toLocaleString()}</span>
            </div>
            <div className="gen-progress__track"><div className="gen-progress__bar" /></div>
          </div>
        )}
        {/* WS-ERR F-9 — this browser cannot keep mock-ups on disk (blocked site data, a
            private window, a full disk). The run still works: the bytes are in memory for as
            long as the app stays open. It just has to be SAID, because after a restart every
            template has to download again and nothing else would explain why. Warn tier,
            `role="status"`: nothing has failed. */}
        {storeDegraded && (
          <div className="banner banner--warn" role="status" data-testid="mockup-store-memory-only">
            Mock-ups are kept in memory on this browser — this run will work, but they will not
            survive closing the app and will download again next time. (Storage is unavailable:{" "}
            {storeDegraded}.)
          </div>
        )}

        {/* WS-P: the run is refused BEFORE it starts, naming every template whose mock-up did
            not land, with the download that fixes it. Warn tier: nothing has failed yet. */}
        <MissingMockups missing={missing} testId="mockup-bake-blocked" />
        {/* Rule 7: the funnel's commit, in the shared `.actionbar` instead of the
            `.mockup-footer__summary` row this surface invented for itself. The count leads
            the bar and the primitive places the ONE primary last. */}
        <ActionBar label="Selection" primary={
          <button type="button" className="btn btn--primary" disabled={!canProceed}
                  onClick={() => onReady?.()} data-testid="mockup-funnel-continue">
            Continue to batch
          </button>
        }>
          <b data-testid="mockup-ready-count">
            {batchSelections.length} template{batchSelections.length === 1 ? "" : "s"} selected
          </b>
          <SelectedList selections={batchSelections} onRemove={unstage} />
        </ActionBar>
      </div>
      )}
    </div>
  );
}

/** The staged templates, one removable chip each, on a single line that scrolls sideways so
 *  the bar keeps its height however many are staged. */
function SelectedList({ selections, onRemove }: {
  selections: TemplateSelection[];
  onRemove: (templateId: string) => void;
}) {
  if (selections.length === 0) return null;
  return (
    <ul className="mockup-selected" aria-label="Selected templates" data-testid="mockup-selected-list">
      {selections.map((s) => (
        <li key={s.templateId} className="mockup-selected__item" title={s.name} data-testid="mockup-selected-item">
          <span className="mockup-selected__model">{s.context.model ?? s.name}</span>
          <span className="faint">{s.code ?? s.name}</span>
          <button type="button" className="mockup-selected__remove" aria-label={`Remove ${s.name}`}
                  onClick={() => onRemove(s.templateId)} data-testid="mockup-selected-remove">
            <Icon name="close" size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * One template row. Selection (the checkbox) is instant/synchronous; the baked-asset HYDRATION
 * state is display-only — it subscribes to the per-version status the hydrate wrapper publishes
 * (Task A1's `useHydrationStore`) and surfaces it WITHOUT gating the toggle:
 *  - hydrating → a `.btn-spinner` + `aria-busy` on the row (assets are still coming down);
 *  - failed    → an `InlineRetry` that force-re-runs hydration (bypasses the 60s failure memo).
 * Absent status (idle / ready) renders nothing extra. Kept as its own component so each row can
 * hold its own store subscription (a hook can't run inside the `.map`).
 */
function TemplateRow({
  t, on, offDevice, pulling, onToggle, onRetry,
}: {
  t: ProductTemplate;
  on: boolean;
  /** No full record here yet — this row came from the cloud listing (F-8). */
  offDevice: boolean;
  /** Its record is downloading right now, because the operator just ticked it. */
  pulling: boolean;
  onToggle: (next: boolean) => void;
  onRetry: () => void;
}) {
  const versionId = t.currentVersion?.versionId;
  const status = useHydrationStore((s) => (versionId ? s.status[versionId] : undefined));
  const hydrating = status?.state === "hydrating";
  const failed = status?.state === "failed";
  // D-28's vocabulary, not a second one: the Templates page already says exactly this about
  // exactly this state, and two sentences for one fact is how a vocabulary rots.
  const badge = offDevice ? presenceBadge("not-synced") : null;
  return (
    <li className="mockup-template">
      <label className={`mockup-template__row${on ? " mockup-template__row--on" : ""}`}
             aria-busy={hydrating || pulling || undefined}>
        <input type="checkbox" className="mockup-template__check"
               checked={on}
               onChange={(e) => onToggle(e.target.checked)}
               data-testid="mockup-template-check" aria-label={t.name} />
        <span className="mockup-template__name">{t.name}</span>
        {badge && (
          <span className={badge.className} title={badge.title}
                data-testid="mockup-template-off-device" data-on-device="false">
            {badge.label}
          </span>
        )}
        {(hydrating || pulling) && (
          <span className="btn-spinner" aria-hidden="true" data-testid="hydrate-spinner" />
        )}
        <span className="mockup-template__status">{t.currentVersion?.status}</span>
      </label>
      {/* Retry sits OUTSIDE the <label> so its click doesn't toggle the checkbox. */}
      {failed && (
        <div className="mockup-template__hydrate-error">
          <InlineRetry onRetry={onRetry} message="Layers failed to load" label="Retry"
                       testId="hydrate-retry" />
        </div>
      )}
    </li>
  );
}
